import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import AdminDataTable from "../../components/AdminDataTable";
import {
  AdminRowActionsMenu,
  buildActionsColumn,
  useAdminRowActionsMenu,
} from "../../components/AdminRowActions";
import { createFaculty, fetchFaculty, updateFaculty } from "../../../lib/api";

function emptyForm() {
  return {
    name: "",
    post: "",
    academicQualifications: [""],
    professionalQualifications: [""],
  };
}

function normalizeQualificationList(value) {
  if (Array.isArray(value)) {
    const items = value.map((s) => String(s).trim()).filter(Boolean);
    return items.length ? items : [""];
  }
  const single = String(value ?? "").trim();
  return single ? [single] : [""];
}

function qualificationsForApi(values) {
  return values.map((s) => String(s).trim()).filter(Boolean);
}

function formatQualificationsCell(row, field) {
  const plural = row[`${field}s`];
  const list = Array.isArray(plural)
    ? plural.filter(Boolean)
    : String(row[field] ?? "")
        .split(/[,;|\n]/)
        .map((s) => s.trim())
        .filter(Boolean);
  if (!list.length) return "—";
  return list.join(", ");
}

function cellTitle(value) {
  const t = String(value ?? "").trim();
  return t && t !== "—" ? t : undefined;
}

function rowIsActive(row) {
  return row.active !== false;
}

function QualificationFields({ label, values, onChange, readOnly }) {
  const updateAt = (index, value) => {
    const next = [...values];
    next[index] = value;
    onChange(next);
  };

  const addRow = () => onChange([...values, ""]);

  const removeAt = (index) => {
    if (values.length <= 1) {
      onChange([""]);
      return;
    }
    onChange(values.filter((_, i) => i !== index));
  };

  return (
    <div className="adm-field adm-field--list">
      <span className="adm-field-label">{label}</span>
      <div className="adm-qual-list">
        {values.map((val, index) => (
          <div className="adm-qual-row" key={`${label}-${index}`}>
            <input
              className="adm-input"
              value={val}
              onChange={(e) => updateAt(index, e.target.value)}
              placeholder={index === 0 ? "e.g. M.Ed." : "Add another…"}
              readOnly={readOnly}
              disabled={readOnly}
            />
            {!readOnly && values.length > 1 ? (
              <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => removeAt(index)} aria-label="Remove">
                Remove
              </button>
            ) : null}
          </div>
        ))}
        {!readOnly ? (
          <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm adm-qual-add" onClick={addRow}>
            + Add {label.toLowerCase()}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function AdminFacultyList() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");
  const [formMode, setFormMode] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");
  const rowActions = useAdminRowActionsMenu();

  const reload = useCallback(async () => {
    setLoading(true);
    setListError("");
    try {
      const res = await fetchFaculty();
      setRecords(res.data || []);
    } catch (err) {
      setListError(err.message || "Could not load faculty.");
      if (err.status === 401) navigate("/admin/login", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    reload();
  }, [reload]);

  const counts = useMemo(() => {
    const active = records.filter((r) => rowIsActive(r)).length;
    return { all: records.length, active, inactive: records.length - active };
  }, [records]);

  const filtered = useMemo(() => {
    if (filter === "active") return records.filter((r) => rowIsActive(r));
    if (filter === "inactive") return records.filter((r) => !rowIsActive(r));
    return records;
  }, [records, filter]);

  const sortedRows = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const ao = Number(a.sortOrder) || 0;
      const bo = Number(b.sortOrder) || 0;
      if (ao !== bo) return ao - bo;
      return String(a.name || "").localeCompare(String(b.name || ""));
    });
  }, [filtered]);

  const openCreate = () => {
    setFormError("");
    setForm(emptyForm());
    setEditingId(null);
    setFormMode("create");
  };

  const openEdit = (row, mode = "edit") => {
    setFormError("");
    setForm({
      name: row.name ?? "",
      post: row.post ?? row.designation ?? "",
      academicQualifications: normalizeQualificationList(
        row.academicQualifications ?? row.academicQualification
      ),
      professionalQualifications: normalizeQualificationList(
        row.professionalQualifications ?? row.professionalQualification
      ),
    });
    setEditingId(row.id);
    setFormMode(mode);
  };

  const openView = (row) => openEdit(row, "view");

  useEffect(() => {
    if (formMode) rowActions.setActionMenu(null);
  }, [formMode, rowActions]);

  const closeForm = useCallback(() => {
    setFormMode(null);
    setEditingId(null);
    setForm(emptyForm());
    setFormError("");
  }, []);

  useEffect(() => {
    if (!formMode) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeForm();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [formMode, closeForm]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!String(form.name ?? "").trim()) {
      setFormError("Faculty name is required.");
      return;
    }
    if (!String(form.post ?? "").trim()) {
      setFormError("Post is required.");
      return;
    }

    const payload = {
      name: String(form.name).trim(),
      post: String(form.post).trim(),
      academicQualifications: qualificationsForApi(form.academicQualifications),
      professionalQualifications: qualificationsForApi(form.professionalQualifications),
    };

    setSaving(true);
    setFormError("");
    try {
      if (formMode === "edit" && editingId != null) {
        await updateFaculty(editingId, payload);
      } else {
        await createFaculty(payload);
      }
      closeForm();
      await reload();
    } catch (err) {
      setFormError(err.message || "Could not save.");
      if (err.status === 401) navigate("/admin/login", { replace: true });
    } finally {
      setSaving(false);
    }
  };

  const setFacultyActive = async (row, active) => {
    try {
      await updateFaculty(row.id, { active });
      await reload();
    } catch (err) {
      setListError(err.message || "Could not update status.");
      if (err.status === 401) navigate("/admin/login", { replace: true });
    }
  };

  const facultyColumns = [
    {
      id: "index",
      header: "Index no.",
      width: "8%",
      align: "center",
      tdClassName: "adm-data-cell-muted",
      render: (_row, rowIndex) => rowIndex + 1,
    },
    {
      id: "name",
      header: "Name",
      width: "17%",
      tdClassName: "adm-data-cell-name",
      render: (row) => {
        const name = row.name || "—";
        return (
          <strong title={cellTitle(name)}>
            {name}
          </strong>
        );
      },
    },
    {
      id: "academicQualification",
      header: "Academic Qualification",
      width: "19%",
      tdClassName: "adm-data-cell-muted",
      render: (row) => {
        const text = formatQualificationsCell(row, "academicQualification");
        return <span title={cellTitle(text)}>{text}</span>;
      },
    },
    {
      id: "professionalQualification",
      header: "Professional Qualification",
      width: "21%",
      tdClassName: "adm-data-cell-muted",
      render: (row) => {
        const text = formatQualificationsCell(row, "professionalQualification");
        return <span title={cellTitle(text)}>{text}</span>;
      },
    },
    {
      id: "post",
      header: "Post",
      width: "13%",
      render: (row) => {
        const p = String(row.post ?? row.designation ?? "").trim() || "—";
        return <span title={cellTitle(p)}>{p}</span>;
      },
    },
    {
      id: "status",
      header: "Status",
      width: "13%",
      align: "center",
      render: (row) => (
        <span className={`adm-status-pill${rowIsActive(row) ? " adm-status-pill--active" : " adm-status-pill--inactive"}`}>
          {rowIsActive(row) ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      ...buildActionsColumn({
        rowActions,
        getRowLabel: (row) => row.name || "faculty member",
      }),
      width: "9%",
    },
  ];

  const isView = formMode === "view";

  return (
    <div className="adm-page adm-page--wide adm-page--faculty">
      <header className="adm-page-head adm-page-head--row">
        <h1 className="adm-page-title">Faculty list</h1>
        <button type="button" className="adm-btn adm-btn--primary" onClick={openCreate} disabled={Boolean(formMode)}>
          Add faculty
        </button>
      </header>

      {listError ? (
        <p className="adm-field-error" role="alert">
          {listError}
        </p>
      ) : null}

      <div className="adm-banner-toolbar adm-banner-toolbar--filters-only">
        <div className="adm-banner-toolbar-left" role="tablist" aria-label="Filter faculty by status">
          <button
            type="button"
            role="tab"
            aria-selected={filter === "active"}
            className={`adm-filter-tab${filter === "active" ? " adm-filter-tab--active" : ""}`}
            onClick={() => setFilter("active")}
          >
            Active ({counts.active})
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={filter === "inactive"}
            className={`adm-filter-tab${filter === "inactive" ? " adm-filter-tab--active" : ""}`}
            onClick={() => setFilter("inactive")}
          >
            Inactive ({counts.inactive})
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={filter === "all"}
            className={`adm-filter-tab${filter === "all" ? " adm-filter-tab--active" : ""}`}
            onClick={() => setFilter("all")}
          >
            All ({counts.all})
          </button>
        </div>
      </div>

      {formMode &&
        createPortal(
          <div className="adm-drawer-root">
            <button type="button" className="adm-drawer-backdrop" aria-label="Close panel" onClick={closeForm} />
            <aside className="adm-drawer-panel" role="dialog" aria-modal="true" aria-labelledby="adm-faculty-drawer-title">
              <div className="adm-drawer-panel-inner">
                <header className="adm-drawer-header">
                  <h2 id="adm-faculty-drawer-title" className="adm-drawer-title">
                    {isView ? "View faculty" : formMode === "edit" ? "Edit faculty" : "Add faculty"}
                  </h2>
                  <button type="button" className="adm-drawer-close" onClick={closeForm} aria-label="Close">
                    <span aria-hidden>×</span>
                  </button>
                </header>
                <form className="adm-drawer-form" onSubmit={onSubmit}>
                  <div className="adm-drawer-fields">
                    <label className="adm-field">
                      <span className="adm-field-label">
                        Faculty name<span className="adm-req"> *</span>
                      </span>
                      <input
                        className="adm-input"
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        autoFocus={!isView}
                        readOnly={isView}
                        disabled={isView}
                      />
                    </label>

                    <QualificationFields
                      label="Academic qualification"
                      values={form.academicQualifications}
                      onChange={(academicQualifications) => setForm((f) => ({ ...f, academicQualifications }))}
                      readOnly={isView}
                    />

                    <QualificationFields
                      label="Professional qualification"
                      values={form.professionalQualifications}
                      onChange={(professionalQualifications) => setForm((f) => ({ ...f, professionalQualifications }))}
                      readOnly={isView}
                    />

                    <label className="adm-field">
                      <span className="adm-field-label">
                        Post<span className="adm-req"> *</span>
                      </span>
                      <input
                        className="adm-input"
                        value={form.post}
                        onChange={(e) => setForm((f) => ({ ...f, post: e.target.value }))}
                        placeholder="e.g. TGT English"
                        readOnly={isView}
                        disabled={isView}
                      />
                    </label>

                    {formError ? (
                      <p className="adm-field-error" role="alert">
                        {formError}
                      </p>
                    ) : null}
                  </div>
                  <footer className="adm-drawer-footer">
                    <button type="button" className="adm-btn adm-btn--ghost" onClick={closeForm}>
                      {isView ? "Close" : "Cancel"}
                    </button>
                    {!isView ? (
                      <button type="submit" className="adm-btn adm-btn--primary" disabled={saving}>
                        {saving ? "Saving…" : formMode === "edit" ? "Save changes" : "Save"}
                      </button>
                    ) : null}
                  </footer>
                </form>
              </div>
            </aside>
          </div>,
          document.body
        )}

      <AdminRowActionsMenu
        actionMenu={rowActions.actionMenu}
        onClose={rowActions.close}
        ariaLabel="Faculty actions"
        isActive={rowActions.actionMenu ? rowIsActive(rowActions.actionMenu.row) : false}
        onView={(row) => openView(row)}
        onUpdate={(row) => openEdit(row)}
        onToggleActive={(row) => setFacultyActive(row, !rowIsActive(row))}
      />

      <section className="adm-card adm-card--table adm-card--faculty-table">
        {loading ? (
          <p className="adm-empty">Loading…</p>
        ) : (
          <AdminDataTable
            layout="fixed"
            tableClassName="adm-table--sentence-headers adm-table--faculty-rows"
            columns={facultyColumns}
            rows={sortedRows}
            getRowKey={(r) => r.id}
            emptyState={
              records.length === 0 ? (
                <span className="adm-empty adm-empty--in-table">No faculty yet. Use &quot;Add faculty&quot; to add one.</span>
              ) : (
                <span className="adm-empty adm-empty--in-table">No faculty members match this filter.</span>
              )
            }
          />
        )}
      </section>
    </div>
  );
}
